<script setup lang="ts">
import { useQuery } from "@vue/apollo-composable";
import { keysTyped } from "~/helpers/common-helpers";
import { SearchDocument, TypeOf } from "~/graphql/generated/graphql";
import { useSearchStore, type SearchEntry, type SearchType } from "~/composables/stores/search";

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },

  sanitizeValueFunction: {
    type: Function,
    default: null,
  },

  includedTypes: {
    type: Array as PropType<TypeOf[]>,
    default: () => [TypeOf.AssetManagementPortfolio, TypeOf.AssetManagementProperty, TypeOf.AssetManagementTenancy],
  },
});

const emit = defineEmits<{ (event: "item-selected"): void }>();

const debouncing = ref(false);
const searchTermQuery = ref("");

const t = useTranslator();
const searchStore = useSearchStore();

const searchQuery = useQuery(
  SearchDocument,
  () => ({
    input: {
      text: searchTermQuery.value,
      limit: 16,
      offset: 0,
      includedTypes: props.includedTypes,
    },
  }),
  () => ({ enabled: !!searchTermQuery.value && !searchStore.showingSelected, errorPolicy: "ignore" })
);

const searchResults = computed(() => {
  const result = searchQuery.result.value;

  if (!result?.search) return;

  let resultsOut: SearchEntry[] = [];

  result.search.hits.forEach((result) => {
    resultsOut.push({
      type: result.typeOf as SearchType,
      search: {
        id: result.entityID,
        name: result.text,
      },
      fields: result.fields,
    });
  });

  return resultsOut.sort(sortSearches);
});

const loading = computed(() => searchQuery.loading.value || debouncing.value);
const showingRecentSearches = computed(() => searchTermQuery.value != undefined && searchTermQuery.value.length <= 2);
const showingNoResults = computed(() => !loading.value && !showingRecentSearches.value && combinedResults.value && combinedResults.value.length === 0);
const combinedResults = computed(() => (searchStore.showingSelected ? [] : showingRecentSearches.value ? lastSearches.value : searchResults.value));

const select = (item: SearchEntry, index: number) => {
  searchStore.selection = index;
  searchStore.selectItem();

  pushToLastSearches(item);

  emit("item-selected");
};

const removeSearch = (search: SearchEntry) => {
  const index = lastSearches.value.findIndex((_search) => searchIsEqual(search, _search));

  if (index === -1) return;

  let temp = [...lastSearches.value];

  temp.splice(index, 1);

  lastSearches.value = temp;

  localStorage.setItem("last-searches", JSON.stringify(temp));
};

const sortSearches = (a: SearchEntry, b: SearchEntry) => {
  //first sort by portfolio
  if (a.type === "ASSET_MANAGEMENT_PORTFOLIO" && b.type !== "ASSET_MANAGEMENT_PORTFOLIO") {
    return -1;
  }
  if (a.type !== "ASSET_MANAGEMENT_PORTFOLIO" && b.type === "ASSET_MANAGEMENT_PORTFOLIO") {
    return 1;
  }

  // then sort by asset management property
  if (a.type === "ASSET_MANAGEMENT_PROPERTY" && b.type !== "ASSET_MANAGEMENT_PROPERTY") {
    return -1;
  }
  if (a.type !== "ASSET_MANAGEMENT_PROPERTY" && b.type === "ASSET_MANAGEMENT_PROPERTY") {
    return 1;
  }

  // then sort by asset management unit
  if (a.type === "ASSET_MANAGEMENT_TENANCY" && b.type !== "ASSET_MANAGEMENT_TENANCY") {
    return -1;
  }
  if (a.type !== "ASSET_MANAGEMENT_TENANCY" && b.type === "ASSET_MANAGEMENT_TENANCY") {
    return 1;
  }

  return 0;
};

const lastSearches = ref<SearchEntry[]>([]);

const searchIsEqual = (a: SearchEntry, b: SearchEntry) => keysTyped(a.search).every((key) => a.search[key] === b.search[key]);

const pushToLastSearches = (search: SearchEntry) => {
  if (!search) return;

  let temp = [...lastSearches.value];

  const placement = temp.findIndex((srch) => searchIsEqual(srch, search));

  if (placement >= 0) temp.splice(placement, 1);

  temp.unshift(search);

  temp = temp.slice(0, 5);

  lastSearches.value = temp;

  localStorage.setItem("last-searches", JSON.stringify(temp));
};

const debounce = useDebounce((val: string) => {
  debouncing.value = false;

  searchTermQuery.value = val;
}, 500);

watch(
  computed(() => searchStore.searchTerm),
  (val) => {
    debouncing.value = true;
    debounce(val);
  }
);

watch(combinedResults, (v) => (searchStore.results = v ?? []), { immediate: true });

watch(
  () => props.show,
  () => {
    fetchStoredSearches();
  },
  { immediate: true }
);

function fetchStoredSearches() {
  try {
    const localStorageItems: SearchEntry[] = localStorage.getItem("last-searches") ? JSON.parse(localStorage.getItem("last-searches") ?? "[]") : [];

    if (Array.isArray(localStorageItems))
      lastSearches.value = localStorageItems.filter(
        (item) =>
          typeof item === "object" &&
          (item.type === "ASSET_MANAGEMENT_PORTFOLIO" || item.type === "ASSET_MANAGEMENT_PROPERTY" || item.type === "ASSET_MANAGEMENT_TENANCY") &&
          item.search.name
      );
  } catch {}
}
</script>

<template>
  <ChromeAutocomplete v-if="show" :labelText="showingRecentSearches ? t('SEARCHBAR_LATEST_SEARCHES') : ''">
    <template #items>
      <Composition :loading="!showingRecentSearches && loading" loadingEnabled loadingColor="--color-accent-200">
        <template #layouts>
          <CompositionLayoutSingle v-if="showingNoResults">
            <template #container>
              <Copy type="label" size="small" color="--color-accent-500">
                {{ t("FILTER_NO_RESULTS") }}
              </Copy>
            </template>
          </CompositionLayoutSingle>
        </template>
      </Composition>

      <SimpleTable v-if="showingRecentSearches || !loading">
        <ChromeAutocompleteItem
          v-for="(item, i) in combinedResults"
          :key="`autocomplete-${i}`"
          :highlightText="searchStore.searchTerm"
          :type="item.type"
          :labelText="item.search.name"
          :fields="item.fields"
          :selected="searchStore.selection == i"
          :clearIconVisible="showingRecentSearches"
          @click="() => select(item, i)"
          @click-clear="() => removeSearch(item)"
        />
      </SimpleTable>
    </template>
  </ChromeAutocomplete>
</template>
